import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock, RotateCcw, Trash2, X } from "lucide-react";

export default function TrashDialog({ open, onOpenChange, boardId, currentUser, board, onCardRestored }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !boardId) return;
    setLoading(true);
    api.get(`/boards/${boardId}/trash`)
      .then(({ data }) => setCards(data || []))
      .catch(() => toast.error("Failed to load trash"))
      .finally(() => setLoading(false));
  }, [open, boardId]);

  const restore = async (cardId) => {
    try {
      await api.post(`/cards/${cardId}/restore`);
      setCards((cs) => cs.filter((c) => c.id !== cardId));
      toast.success("Card restored");
      onCardRestored && onCardRestored(cardId);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to restore");
    }
  };

  const permanentDelete = async (cardId) => {
    if (!window.confirm("Permanently delete this card? This cannot be undone.")) return;
    try {
      await api.delete(`/trash/permanently-delete?card_id=${cardId}`);
      setCards((cs) => cs.filter((c) => c.id !== cardId));
      toast.success("Card permanently deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const canManage = (cardItem) => currentUser && board && (
    currentUser.id === board.owner_id || currentUser.role === "admin" || currentUser.id === cardItem.created_by
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-zinc-200 dark:border-zinc-800 sm:max-w-lg w-full sm:w-auto h-[100dvh] sm:h-auto sm:max-h-[85vh] overflow-y-auto p-0">
        <DialogHeader className="p-4 sm:p-6 pb-2 border-b border-zinc-200 dark:border-zinc-800 sticky top-0 bg-white dark:bg-zinc-950 z-10">
          <DialogTitle className="font-display text-xl font-bold tracking-tight flex items-center gap-2 text-zinc-950 dark:text-zinc-100">
            <Clock className="h-5 w-5" /> Recently Deleted
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-sm text-zinc-500 text-center py-8">Loading...</div>
          ) : cards.length === 0 ? (
            <div className="text-sm text-zinc-400 text-center py-8">
              <Clock className="h-8 w-8 mx-auto mb-3 opacity-50" />
              No cards in trash. Deleted cards appear here for 15 days.
            </div>
          ) : (
            <div className="space-y-2">
              {cards.map((c) => (
                <div
                  key={c.id}
                  className="border border-zinc-200 dark:border-zinc-800 p-3 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-950 dark:text-zinc-100 truncate">{c.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>Deleted {new Date(c.deleted_at).toLocaleDateString()}</span>
                      {c.created_by_name && <span>by {c.created_by_name}</span>}
                      {c.labels && c.labels.length > 0 && (
                        <span className="inline-flex gap-1">
                          {c.labels.slice(0, 3).map((l) => (
                            <span key={l} className="text-[10px] uppercase tracking-wider border border-zinc-300 dark:border-zinc-600 px-1">{l}</span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                  {canManage(c) && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => restore(c.id)}
                        className="p-2 text-zinc-500 hover:text-green-600 transition-colors"
                        title="Restore card"
                        data-testid={`restore-card-${c.id}`}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => permanentDelete(c.id)}
                        className="p-2 text-zinc-500 hover:text-red-600 transition-colors"
                        title="Permanently delete"
                        data-testid={`permanent-delete-${c.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
